import { Injectable, inject } from "@angular/core";
import { Firestore } from "@angular/fire/firestore";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Action, State, StateContext, Store } from "@ngxs/store";
import { CollectionName, CollectionStateModel } from "./collection-model.interface";
import { Collection } from "./collection.actions";
import {
  collection, CollectionReference,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
} from "firebase/firestore";

const generateBlankCollectionState = (): CollectionStateModel => {
  return {
    owners: [],
  };
};

@State<CollectionStateModel>({
  name: "collectionState",
  defaults: {
    ...generateBlankCollectionState(),
  },
})
@Injectable()
export class CollectionState {
  firestore = inject(Firestore);
  snackbar = inject(MatSnackBar);
  store = inject(Store);

  collectionRefMap: {[collectionName in keyof CollectionStateModel]: CollectionReference} = {
    "owners": collection(
      this.firestore,
      "owners"
    )
  }

  @Action(Collection.Fetch)
  async fetchCollection<T extends CollectionName>(
    ctx: StateContext<CollectionStateModel>,
    action: Collection.Fetch<T>
  ) {
    try {
      const limitSize = 1000;

      const { collectionName, startAfterDoc } = action;

      const collectionRef = this.collectionRefMap[collectionName];

      let dataQuery = query(
        collectionRef,
        orderBy("lastModifiedDate", "desc"),
        limit(limitSize)
      );

      if (startAfterDoc) {
        dataQuery = query(dataQuery, startAfter(startAfterDoc));
      }

      const documentSnapshots = await getDocs(dataQuery);

      if (documentSnapshots.empty) {
        console.log("No more documents to load.");
        return
      }

      const lastVisible =
        documentSnapshots.docs[documentSnapshots.docs.length - 1];

      ctx.setState({
        ...ctx.getState(),
        [collectionName]: [
          ...ctx.getState()[collectionName],
          ...documentSnapshots.docs.map((doc) => doc.data() as CollectionStateModel[T]),
        ],
      });

      ctx.dispatch(new Collection.Fetch(collectionName, lastVisible));
    } catch (error) {
      console.error("Error fetching documents: ", error);
    }
  }

  @Action(Collection.SetCollection)
  async setCollection<T extends CollectionName>(
    ctx: StateContext<CollectionStateModel>,
    action: Collection.SetCollection<T>
  ) {
    const { collectionName, data } = action;

    ctx.setState({
      ...ctx.getState(),
      [collectionName]: data,
    });
  }
}
